// Tiny inline SVG sparkline — no chart library needed.
// Usage:  <MiniSparkline label="SBP" unit="mmHg" points={[{v:140,d:'2026-01-10'}, ...]} betterDown />
//
// • `points` is oldest→newest.
// • `betterDown` controls arrow colour semantics (weight/BP/HbA1c/LDL are
//   better-down; HDL/Hb are better-up; leave undefined for neutral).

import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function MiniSparkline({ label, unit, points = [], betterDown, width = 120, height = 32 }) {
  const vals = points.map((p) => Number(p.v)).filter((n) => Number.isFinite(n));
  if (vals.length < 2) {
    return (
      <div className="text-[10px] text-slate-400 border border-dashed border-slate-200 rounded px-2 py-1.5" data-testid={`spark-${label.replace(/\s+/g, "-").toLowerCase()}-empty`}>
        <div className="font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div>needs ≥2 visits</div>
      </div>
    );
  }
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const stepX = width / Math.max(vals.length - 1, 1);
  const pts = vals.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const polyline = pts.join(" ");
  const last = vals[vals.length - 1];
  const prev = vals[vals.length - 2];
  const delta = last - prev;
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  let tone = "text-slate-500";
  if (delta !== 0 && betterDown !== undefined) {
    const improved = (betterDown && delta < 0) || (betterDown === false && delta > 0);
    tone = improved ? "text-emerald-600" : "text-red-600";
  }
  const lastX = (vals.length - 1) * stepX;
  const lastY = height - ((last - min) / range) * (height - 6) - 3;

  return (
    <div className="border border-slate-200 rounded px-2 py-1.5 bg-white" data-testid={`spark-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-baseline justify-between gap-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`text-[11px] tabular font-medium ${tone} flex items-center gap-0.5`}>
          <Icon size={10} />
          <span className="text-slate-800">{last}</span>
          {unit && <span className="text-slate-400 text-[9px]">{unit}</span>}
        </div>
      </div>
      <svg width={width} height={height} className="mt-0.5" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-pink-600"
          points={polyline}
        />
        {vals.map((v, i) => {
          const x = i * stepX;
          const y = height - ((v - min) / range) * (height - 6) - 3;
          return <circle key={i} cx={x} cy={y} r="1.5" className="fill-pink-600" />;
        })}
        <circle cx={lastX} cy={lastY} r="2.5" className="fill-pink-700 stroke-white" strokeWidth="1" />
      </svg>
      <div className="flex justify-between text-[9px] text-slate-400 tabular mt-0.5">
        <span>{vals[0]}</span>
        <span>{vals.length} visits</span>
      </div>
    </div>
  );
}
